import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage, useAuth, api } from '../context/AppContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import RecipeImage from '../components/RecipeImage';
import RecipeSavingsAlert from '../components/RecipeSavingsAlert';
import { 
  Search, 
  BookOpen, 
  Plus, 
  Loader2, 
  ChefHat, 
  Clock, 
  Users, 
  Trash2,
  Zap,
  Flame,
  ExternalLink,
  Check,
  Filter,
  X,
  Utensils,
  Edit3,
  Save,
  PlusCircle,
  MinusCircle,
  Package,
  Sparkles,
  Target,
  TrendingUp,
  TrendingDown,
  Minus,
  BadgeDollarSign
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import ExpressRecipesWidget from '../components/ExpressRecipesWidget';

const RecipeBook = () => {
  const { language, t } = useLanguage();
  const { user } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [showRecipeDetail, setShowRecipeDetail] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [savingRecipe, setSavingRecipe] = useState(null);
  const [cookingRecipe, setCookingRecipe] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterHighProtein, setFilterHighProtein] = useState(false);
  const [localSearch, setLocalSearch] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState('myRecipes'); // 'myRecipes' or 'express'
  const [analyzingMacros, setAnalyzingMacros] = useState(null);
  const [previewRecipe, setPreviewRecipe] = useState(null); // For previewing search results
  const [macroAnalysisResult, setMacroAnalysisResult] = useState(null); // For showing analysis modal

  // Callback for RecipeImage component to update recipe image
  const handleImageUpdate = useCallback((recipeId, imageUrl, imageSource) => {
    setRecipes(prev => prev.map(r => 
      r.recipe_id === recipeId 
        ? { ...r, image_url: imageUrl, image_source: imageSource, image_status: 'ready' }
        : r
    ));
    
    if (selectedRecipe?.recipe_id === recipeId) {
      setSelectedRecipe(prev => ({
        ...prev,
        image_url: imageUrl,
        image_source: imageSource
      }));
    }
  }, [selectedRecipe?.recipe_id]);

  useEffect(() => {
    fetchRecipes();
  }, [filterCategory, filterHighProtein]); // eslint-disable-line react-hooks/exhaustive-deps -- fetchRecipes defined below, stable deps

  const fetchRecipes = async () => {
    setLoading(true);
    try {
      let url = '/recipes?';
      if (filterCategory !== 'all') url += `category=${filterCategory}&`;
      if (filterHighProtein) url += `high_protein=true&`;
      if (localSearch) url += `search=${localSearch}&`;
      
      const response = await api.get(url);
      setRecipes(response.data.recipes || []);
    } catch (error) {
      console.error('Error fetching recipes:', error);
      toast.error(t('recipebook.erreur_de_chargement', 'Erreur de chargement'));
    } finally {
      setLoading(false);
    }
  };

  // Search progress state
  const [searchProgress, setSearchProgress] = useState(0);
  const [searchStatus, setSearchStatus] = useState('');
  const searchAbortRef = useRef(null);

  const handleWebSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    setSearchResults([]);
    setSearchProgress(0);
    setSearchStatus(t('recipebook.connexion_a_yumix', 'Connexion a YUMIX...'));

    try {
      const sessionToken = localStorage.getItem('session_token');
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/recipes/search-stream?query=${encodeURIComponent(searchQuery)}&high_protein=${filterHighProtein}`;
      
      const controller = new AbortController();
      searchAbortRef.current = controller;
      
      // Safety timeout: 30 seconds
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      const response = await fetch(url, {
        headers: { 'X-Session-Token': sessionToken },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Check response status before reading stream
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      const recipes = [];
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            
            if (event.type === 'status') {
              setSearchStatus(event.message);
              setSearchProgress(prev => Math.min(prev + 15, 60));
            } else if (event.type === 'recipe') {
              recipes.push(event.recipe);
              setSearchResults([...recipes]);
              setSearchProgress(60 + Math.round((event.index / event.total) * 35));
              setSearchStatus(t('recipebook.recipe_index_received', 'Recette {{index}}/{{total}} reçue...', { index: event.index + 1, total: event.total }));
              // Auto-switch to AI tab on first recipe
              if (recipes.length === 1) setActiveMainTab('aiSuggestions');
            } else if (event.type === 'done') {
              setSearchProgress(100);
              setSearchStatus(t('recipebook.recipes_generated_count', '{{count}} recettes générées !', { count: event.total }));
            } else if (event.type === 'error') {
              toast.error(event.message);
            }
          } catch {}
        }
      }

      // If stream completed but no recipes arrived, try fallback
      if (recipes.length === 0) {
        throw new Error('No recipes from stream');
      }
    } catch (error) {
      // Iter. 411.j — Fix bug rapporté par Youri (Isabelle voit spinner
      // sans fin). Avant : AbortError (timeout 30s) était ignoré → aucun
      // fallback, aucun toast, spinner s'éteint sans feedback. Maintenant :
      // AbortError déclenche AUSSI le fallback vers /recipes/search + toast.
      const wasTimeout = error.name === 'AbortError';
      if (wasTimeout) {
        setSearchStatus(t('recipebook.timeout_fallback', 'Trop lent, plan B...'));
      } else {
        console.error('Stream error:', error);
        setSearchStatus(t('recipebook.changement_de_methode', 'Changement de méthode...'));
      }
      // Fallback to regular API (works even after AbortError).
      try {
        const response = await api.get(
          `/recipes/search?query=${encodeURIComponent(searchQuery)}&high_protein=${filterHighProtein}`
        );
        const results = response.data.recipes || [];
        setSearchResults(results);
        if (results.length > 0) setActiveMainTab('aiSuggestions');
        setSearchProgress(100);
        setSearchStatus(t('recipebook.recipes_generated_count', '{{count}} recettes générées !', { count: results.length }));
        if (results.length === 0) {
          toast.error(
            wasTimeout
              ? t('recipebook.timeout_no_result', 'Délai dépassé — réessaie avec des mots-clés plus courts.')
              : t('recipebook.erreur_de_recherche_reessaie', 'Erreur de recherche. Réessaie.')
          );
        }
      } catch (e2) {
        toast.error(
          wasTimeout
            ? t('recipebook.timeout_no_result', 'Délai dépassé — réessaie avec des mots-clés plus courts.')
            : t('recipebook.erreur_de_recherche_reessaie', 'Erreur de recherche. Réessaie.')
        );
      }
    } finally {
      setSearching(false);
      searchAbortRef.current = null;
      setTimeout(() => { setSearchProgress(0); setSearchStatus(''); }, 1500);
    }
  };

  const handleCancelSearch = () => {
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
      setSearching(false);
      setSearchProgress(0);
      setSearchStatus('');
    }
  };

  const handleSaveRecipe = async (recipe) => {
    setSavingRecipe(recipe.title);
    try {
      await api.post('/recipes', {
        title: recipe.title,
        description: recipe.description,
        source_url: 'web_search',
        ingredients: recipe.ingredients || [],
        instructions: recipe.instructions || [],
        prep_time: recipe.prep_time,
        servings: recipe.servings,
        protein_g: recipe.estimated_protein_g || 0,
        calories: recipe.estimated_calories || 0,
        carbs_g: recipe.estimated_carbs_g || 0,
        fats_g: recipe.estimated_fats_g || 0,
        fiber_g: recipe.estimated_fiber_g || 0,
        category: 'general',
        tags: recipe.tags || []
      });
      toast.success(t('recipebook.recette_sauvegardee', 'Recette sauvegardée!'));
      fetchRecipes();
    } catch (error) {
      console.error('Error saving recipe:', error);
      toast.error(t('recipebook.erreur_de_sauvegarde', 'Erreur de sauvegarde'));
    } finally {
      setSavingRecipe(null);
    }
  };

  const handleDeleteRecipe = async (recipeId) => {
    setDeleting(true);
    try {
      await api.delete(`/recipes/${recipeId}`);
      toast.success(t('recipebook.recette_supprimee', 'Recette supprimée'));
      setRecipes(prev => prev.filter(r => r.id !== recipeId && r.recipe_id !== recipeId));
      setShowRecipeDetail(false);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting recipe:', error);
      toast.error(t('recipebook.erreur_de_suppression', 'Erreur de suppression'));
    } finally {
      setDeleting(false);
    }
  };

  const openEditDialog = () => {
    if (!selectedRecipe) return;
    setEditForm({
      title: selectedRecipe.title || '',
      description: selectedRecipe.description || '',
      ingredients: selectedRecipe.ingredients || [],
      instructions: selectedRecipe.instructions || [],
      prep_time: selectedRecipe.prep_time || '',
      cook_time: selectedRecipe.cook_time || '',
      servings: selectedRecipe.servings || '',
      protein_g: selectedRecipe.protein_g || 0,
      calories: selectedRecipe.calories || 0,
      carbs_g: selectedRecipe.carbs_g || 0,
      fats_g: selectedRecipe.fats_g || 0,
      fiber_g: selectedRecipe.fiber_g || 0
    });
    setShowEditDialog(true);
    setShowRecipeDetail(false);
  };

  const handleAddIngredient = () => {
    setEditForm(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, '']
    }));
  };

  const handleRemoveIngredient = (index) => {
    setEditForm(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateIngredient = (index, value) => {
    setEditForm(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) => i === index ? value : ing)
    }));
  };

  const handleAddInstruction = () => {
    setEditForm(prev => ({
      ...prev,
      instructions: [...prev.instructions, '']
    }));
  };

  const handleRemoveInstruction = (index) => {
    setEditForm(prev => ({
      ...prev,
      instructions: prev.instructions.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateInstruction = (index, value) => {
    setEditForm(prev => ({
      ...prev,
      instructions: prev.instructions.map((inst, i) => i === index ? value : inst)
    }));
  };

  // Analyze macros with AI
  const handleAnalyzeMacros = async (recipe) => {
    if (!recipe?.recipe_id) return;
    
    setAnalyzingMacros(recipe.recipe_id);
    try {
      const response = await api.post(`/recipes/${recipe.recipe_id}/analyze-macros`);
      
      // Update the recipe in the list
      setRecipes(prev => prev.map(r => 
        r.recipe_id === recipe.recipe_id 
          ? { ...r, nutrition: response.data.nutrition, macro_analysis_notes: response.data.analysis_notes }
          : r
      ));
      
      // Update selected recipe if viewing details
      if (selectedRecipe?.recipe_id === recipe.recipe_id) {
        setSelectedRecipe(prev => ({
          ...prev,
          protein_g: response.data.nutrition.protein_g,
          calories: response.data.nutrition.calories,
          carbs_g: response.data.nutrition.carbs_g,
          fats_g: response.data.nutrition.fats_g,
          fiber_g: response.data.nutrition.fiber_g
        }));
      }
      
      // Show analysis in a modal that stays until user closes it
      setMacroAnalysisResult({
        recipeName: recipe.title,
        nutrition: response.data.nutrition,
        notes: response.data.analysis_notes,
        message: response.data.message
      });
      
    } catch (error) {
      toast.error(t('recipebook.erreur_lors_de_l_analyse', 'Erreur lors de l\\\'analyse'));
    } finally {
      setAnalyzingMacros(null);
    }
  };

  // Auto-calculate calories when macros change
  // Calories = (protein * 4) + (carbs * 4) + (fats * 9)
  const handleMacroChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    
    setEditForm(prev => {
      const newForm = { ...prev, [field]: numValue };
      
      // Recalculate calories
      const protein = field === 'protein_g' ? numValue : (prev.protein_g || 0);
      const carbs = field === 'carbs_g' ? numValue : (prev.carbs_g || 0);
      const fats = field === 'fats_g' ? numValue : (prev.fats_g || 0);
      
      newForm.calories = Math.round((protein * 4) + (carbs * 4) + (fats * 9));
      
      return newForm;
    });
  };

  const handleSaveRecipeEdit = async () => {
    if (!selectedRecipe) return;
    
    setSaving(true);
    try {
      // Filter out empty ingredients and instructions
      const cleanedForm = {
        ...editForm,
        ingredients: editForm.ingredients.filter(ing => ing.trim() !== ''),
        instructions: editForm.instructions.filter(inst => inst.trim() !== '')
      };
      
      // Use id or recipe_id (support both formats)
      const recipeId = selectedRecipe.id || selectedRecipe.recipe_id;
      await api.patch(`/recipes/${recipeId}`, cleanedForm);
      toast.success(t('recipebook.recette_modifiee', 'Recette modifiée!'));
      
      // Update local state (support both id formats)
      setRecipes(prev => prev.map(r => 
        (r.id === recipeId || r.recipe_id === recipeId)
          ? { ...r, ...cleanedForm }
          : r
      ));
      
      // Also update selectedRecipe for immediate UI feedback
      setSelectedRecipe(prev => prev ? { ...prev, ...cleanedForm } : prev);
      
      setShowEditDialog(false);
    } catch (error) {
      console.error('Error updating recipe:', error);
      toast.error(t('recipebook.erreur_de_modification', 'Erreur de modification'));
    } finally {
      setSaving(false);
    }
  };

  const handleCookRecipe = async (recipe) => {
    const recipeId = recipe.id || recipe.recipe_id;
    setCookingRecipe(recipeId);
    try {
      const response = await api.post(`/recipes/${recipeId}/cook`);
      toast.success(response.data.message);
      fetchRecipes();
      setShowRecipeDetail(false);
    } catch (error) {
      console.error('Error cooking recipe:', error);
      toast.error(t('recipebook.erreur', 'Erreur'));
    } finally {
      setCookingRecipe(null);
    }
  };

  const openRecipeDetail = (recipe) => {
    setSelectedRecipe(recipe);
    setShowRecipeDetail(true);
  };

  // Auto-analyze macros when recipe detail opens if macros are zero and ingredients exist
  useEffect(() => {
    if (
      showRecipeDetail &&
      selectedRecipe?.recipe_id &&
      !analyzingMacros &&
      selectedRecipe.ingredients?.length > 0 &&
      !(selectedRecipe.protein_g > 0 || selectedRecipe.calories > 0)
    ) {
      handleAnalyzeMacros(selectedRecipe);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRecipeDetail, selectedRecipe?.recipe_id]);

  const categories = [
    { value: 'all', label: { fr: 'Toutes', en: 'All' } },
    { value: 'breakfast', label: { fr: 'Déjeuner', en: 'Breakfast' } },
    { value: 'lunch', label: { fr: 'Dîner', en: 'Lunch' } },
    { value: 'dinner', label: { fr: 'Souper', en: 'Dinner' } },
    { value: 'snack', label: { fr: 'Collation', en: 'Snack' } },
    { value: 'general', label: { fr: 'Général', en: 'General' } }
  ];

  // Component: L'Assiette Intelligente Différenciée
  // Analyzes recipe for the whole family AND creates a "Focus Client" for the user
  const SmartPlateAnalysis = ({ recipe, isPreview = false }) => {
    // Only show for clients with defined macro targets
    if (!user || user.role === 'coach') return null;
    
    // User's personal targets
    const userCalories = user.daily_calories || 2000;
    const userProteinMin = user.protein_min_g || 100;
    const userProteinMax = user.protein_max_g || 180;
    const userWeight = user.weight_kg || 70; // For protein per kg/lb calculation
    const userWeightLb = userWeight * 2.205; // Convert to lbs
    
    // Family size (total servings to prepare)
    const familySize = user.family_size || 1;
    const familyMembers = user.family_members || [];
    
    // Get recipe values (handle both preview and saved recipe formats)
    const recipeServings = parseInt(recipe.servings) || familySize;
    const totalCalories = isPreview ? (recipe.estimated_calories || 0) : (recipe.calories || 0);
    const totalProtein = isPreview ? (recipe.estimated_protein_g || 0) : (recipe.protein_g || 0);
    const totalCarbs = isPreview ? (recipe.estimated_carbs_g || 0) : (recipe.carbs_g || 0);
    const totalFats = isPreview ? (recipe.estimated_fats_g || 0) : (recipe.fats_g || 0);
    
    // Skip if no valid data
    if (!totalCalories && !totalProtein) return null;
    
    // Calculate CLIENT'S PORTION (1 serving out of total)
    const clientCalories = Math.round(totalCalories / recipeServings);
    const clientProtein = Math.round((totalProtein / recipeServings) * 10) / 10;
    const clientCarbs = Math.round((totalCarbs / recipeServings) * 10) / 10;
    const clientFats = Math.round((totalFats / recipeServings) * 10) / 10;
    
    // Calculate percentages for CLIENT's portion
    const caloriePercent = Math.round((clientCalories / userCalories) * 100);
    const proteinTarget = (userProteinMin + userProteinMax) / 2;
    const proteinPercent = Math.round((clientProtein / proteinTarget) * 100);
    
    // Protein target per meal (aiming for ~33% of daily)
    const mealProteinTarget = proteinTarget / 3;
    const proteinGap = mealProteinTarget - clientProtein;
    const needsMoreProtein = proteinGap > 5; // Need at least 5g more
    
    // Calorie status for a single meal (target: ~33% of daily)
    const mealCalorieTarget = userCalories / 3;
    const calorieStatus = clientCalories <= mealCalorieTarget * 1.1 ? 'good' : clientCalories <= mealCalorieTarget * 1.3 ? 'moderate' : 'high';
    const proteinStatus = clientProtein >= mealProteinTarget * 0.9 ? 'good' : clientProtein >= mealProteinTarget * 0.6 ? 'moderate' : 'low';
    
    // Calculate protein boost suggestions
    const getProteinBoostSuggestion = () => {
      if (!needsMoreProtein) return null;
      const gap = Math.ceil(proteinGap);
      // Protein sources with ~30g protein per 100g
      const chickenGrams = Math.round(gap * 3.3); // ~30g protein per 100g chicken
      const greekYogurtGrams = Math.round(gap * 10); // ~10g protein per 100g
      const eggCount = Math.ceil(gap / 6); // ~6g protein per egg
      
      return {
        gap,
        suggestions: [
          { item: t('recipebook.poulet_grille', 'poulet grillé'), amount: chickenGrams, unit: 'g' },
          { item: t('recipebook.yogourt_grec', 'yogourt grec'), amount: greekYogurtGrams, unit: 'g' },
          { item: t('recipebook.ufs', 'œufs'), amount: eggCount, unit: '' }
        ]
      };
    };
    
    const proteinBoost = getProteinBoostSuggestion();

    return (
      <div className="bg-gradient-to-br from-emerald-500/10 via-blue-500/5 to-purple-500/10 border border-emerald-500/20 rounded-xl p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-emerald-400">
            <Utensils className="w-5 h-5" />
            <span className="font-heading text-sm uppercase tracking-wide">
              {t('recipebook.assiette_intelligente', 'Assiette Intelligente')}
            </span>
          </div>
          <span className="text-xs text-muted-foreground">
            {t('recipebook.recipe_for_servings', 'Recette pour {{servings}} pers.', { servings: recipeServings })}
          </span>
        </div>

        {/* Two columns: Family vs Client */}
        <div className="grid grid-cols-2 gap-3">
          {/* Family Column */}
          <div className="bg-secondary/50 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Users className="w-3 h-3" />
              {t('recipebook.pour_la_famille', 'Pour la famille')}
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Total</span>
                <span className="font-medium">{totalCalories} cal</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Protéines</span>
                <span className="font-medium">{totalProtein}g</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Portions</span>
                <span className="font-medium">{recipeServings} portions</span>
              </div>
            </div>
          </div>
          
          {/* Client Column - Focus */}
          <div className="bg-gradient-to-br from-primary/20 to-primary/10 rounded-lg p-3 space-y-2 border border-primary/30">
            <div className="flex items-center gap-2 text-xs text-primary">
              <Target className="w-3 h-3" />
              {t('recipebook.ton_assiette', 'Ton assiette')}
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Calories</span>
                <span className={`font-bold ${calorieStatus === 'good' ? 'text-green-400' : calorieStatus === 'moderate' ? 'text-amber-400' : 'text-red-400'}`}>
                  {clientCalories} cal
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Protéines</span>
                <span className={`font-bold ${proteinStatus === 'good' ? 'text-green-400' : proteinStatus === 'moderate' ? 'text-amber-400' : 'text-red-400'}`}>
                  {clientProtein}g
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">% journée</span>
                <span className="font-medium text-primary">{caloriePercent}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Progress bars for client */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">{t('recipebook.tes_calories_ce_repas', 'Tes calories ce repas')}</span>
              <span className={`font-medium ${calorieStatus === 'good' ? 'text-green-400' : calorieStatus === 'moderate' ? 'text-amber-400' : 'text-red-400'}`}>
                {caloriePercent}% {t('recipebook.de_ta_journee', 'de ta journée')}
              </span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all ${calorieStatus === 'good' ? 'bg-green-500' : calorieStatus === 'moderate' ? 'bg-amber-500' : 'bg-red-500'}`}
                style={{ width: `${Math.min(caloriePercent, 100)}%` }}
              />
            </div>
          </div>
          
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">{t('recipebook.tes_proteines_ce_repas', 'Tes protéines ce repas')}</span>
              <span className={`font-medium ${proteinStatus === 'good' ? 'text-green-400' : proteinStatus === 'moderate' ? 'text-amber-400' : 'text-red-400'}`}>
                {proteinPercent}% {t('recipebook.de_ta_cible', 'de ta cible')}
              </span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all ${proteinStatus === 'good' ? 'bg-green-500' : proteinStatus === 'moderate' ? 'bg-amber-500' : 'bg-red-500'}`}
                style={{ width: `${Math.min(proteinPercent, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Chef's Tip - Protein Boost Suggestion */}
        {proteinBoost && (
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2 text-amber-400">
              <ChefHat className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wide">
                {t('recipebook.le_petit_plus_du_chef', 'Le petit plus du Chef')}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('recipebook.protein_gap_hint', 'Il te manque ~{{gap}}g de protéines pour atteindre ta cible. Ajoute dans TON assiette :', { gap: proteinBoost.gap })}
            </p>
            <div className="flex flex-wrap gap-2">
              {proteinBoost.suggestions.map((suggestion, i) => (
                <span 
                  key={i}
                  className="px-2 py-1 bg-amber-500/20 text-amber-300 text-xs rounded-full flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  {suggestion.amount}{suggestion.unit} {suggestion.item}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Success message if goals are met */}
        {!needsMoreProtein && proteinStatus === 'good' && calorieStatus === 'good' && (
          <div className="flex items-center gap-2 text-xs p-2 rounded bg-green-500/10 text-green-400">
            <TrendingUp className="w-4 h-4" />
            <span>
              {t('recipebook.parfait_cette_portion_respecte_tes_objec', 'Parfait! Cette portion respecte tes objectifs de performance.')}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="recipe-book">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tight mb-2">
            <span className="text-foreground">{t('recipebook.livre_de', 'Livre de ')}</span>
            <span className="text-primary">{t('recipebook.recettes', 'Recettes')}</span>
          </h1>
          <p className="text-muted-foreground">
            {t('recipebook.recherche_et_sauvegarde_tes_recettes_pre', 'Recherche et sauvegarde tes recettes préférées')}
          </p>
        </motion.div>

        {/* Main Tabs: My Recipes vs Express vs AI Suggestions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-6"
        >
          <div className="flex gap-2 p-1 bg-muted rounded-xl">
            <button
              onClick={() => setActiveMainTab('myRecipes')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                activeMainTab === 'myRecipes' 
                  ? 'bg-card text-foreground shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-my-recipes"
            >
              <BookOpen className="w-4 h-4" />
              {t('recipebook.mes_recettes', 'Mes Recettes')}
              <span className="px-1.5 py-0.5 bg-primary/20 text-primary rounded text-xs">
                {recipes.length}
              </span>
            </button>
            <button
              onClick={() => setActiveMainTab('express')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                activeMainTab === 'express' 
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-express-recipes"
            >
              <Zap className="w-4 h-4" />
              {t('recipebook.express', 'Express')}
              <span className="px-1.5 py-0.5 bg-white/20 rounded text-xs">
                {'<15min'}
              </span>
            </button>
            {/* AI Suggestions Tab - Only visible when there are search results */}
            {searchResults.length > 0 && (
              <button
                onClick={() => setActiveMainTab('aiSuggestions')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                  activeMainTab === 'aiSuggestions' 
                    ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground bg-purple-500/10'
                }`}
                data-testid="tab-ai-suggestions"
              >
                <Sparkles className="w-4 h-4" />
                {t('recipebook.suggestions_du_chef', 'Suggestions du Chef')}
                <span className="px-1.5 py-0.5 bg-white/20 rounded text-xs">
                  {searchResults.length}
                </span>
              </button>
            )}
          </div>
        </motion.div>

        {/* Express Recipes Tab Content */}
        {activeMainTab === 'express' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="pb-24"
          >
            <ExpressRecipesWidget onSaveRecipe={fetchRecipes} />
          </motion.div>
        )}

        {/* AI Suggestions Tab Content */}
        {activeMainTab === 'aiSuggestions' && searchResults.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="pb-24"
          >
            {/* Header with search info */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Sparkles className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl uppercase tracking-tight text-purple-300">
                    {t('recipebook.suggestions_du_chef', 'Suggestions du Chef')}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {t('recipebook.recipes_found_for_query', '{{count}} recettes trouvées pour « {{query}} »', { count: searchResults.length, query: searchQuery })}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleWebSearch}
                  disabled={searching}
                  variant="outline"
                  className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
                  data-testid="refresh-suggestions-btn"
                >
                  {searching ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {t('recipebook.autres_suggestions', 'Autres suggestions')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchResults([]);
                    setActiveMainTab('myRecipes');
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4 mr-1" />
                  {t('recipebook.fermer', 'Fermer')}
                </Button>
              </div>
            </div>

            {/* AI Suggestions Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {searchResults.map((recipe, index) => (
                <div
                  key={`search-${index}`}
                >
                  <Card 
                    className="bg-gradient-to-br from-purple-500/5 to-blue-500/5 border-purple-500/30 hover:border-purple-500/50 transition-colors h-full cursor-pointer relative overflow-hidden"
                    onClick={() => setPreviewRecipe(recipe)}
                  >
                    {/* Chef Badge */}
                    <div className="absolute top-2 right-2 flex gap-1.5">
                      {recipe.has_deal_badge && (
                        <div className="px-2 py-0.5 bg-emerald-500/30 text-emerald-300 text-xs rounded-full flex items-center gap-1" data-testid="deal-badge">
                          <BadgeDollarSign className="w-3 h-3" />
                          {t('recipebook.en_special', 'En spécial')}
                        </div>
                      )}
                      <div className="px-2 py-0.5 bg-purple-500/30 text-purple-300 text-xs rounded-full flex items-center gap-1">
                        <ChefHat className="w-3 h-3" />
                        Chef
                      </div>
                    </div>
                    
                    <CardContent className="p-4 pt-8">
                      <h3 className="font-heading text-lg text-foreground mb-2">{recipe.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{recipe.description}</p>
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        {recipe.prep_time && (
                          <span className="text-xs bg-secondary px-2 py-1 rounded flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {recipe.prep_time}
                          </span>
                        )}
                        {recipe.cook_time && (
                          <span className="text-xs bg-secondary px-2 py-1 rounded flex items-center gap-1">
                            <Flame className="w-3 h-3" /> {recipe.cook_time}
                          </span>
                        )}
                        {recipe.servings && (
                          <span className="text-xs bg-secondary px-2 py-1 rounded flex items-center gap-1">
                            <Users className="w-3 h-3" /> {recipe.servings}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-2 text-sm mb-4">
                        <span className="px-2 py-1 bg-primary/20 text-primary rounded-md font-bold">
                          {recipe.estimated_protein_g || '?'}g prot
                        </span>
                        <span className="px-2 py-1 bg-muted text-muted-foreground rounded-md">
                          {recipe.estimated_calories || '?'} cal
                        </span>
                        {recipe.estimated_carbs_g && (
                          <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded-md text-xs">
                            {recipe.estimated_carbs_g}g gluc
                          </span>
                        )}
                        {recipe.estimated_fats_g && (
                          <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded-md text-xs">
                            {recipe.estimated_fats_g}g lip
                          </span>
                        )}
                      </div>

                      {/* Deal Savings */}
                      {recipe.has_deal_badge && recipe.matching_deals?.length > 0 && (
                        <div className="mb-3 px-2.5 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                          <p className="text-xs text-emerald-400 font-medium flex items-center gap-1">
                            <BadgeDollarSign className="w-3.5 h-3.5" />
                            {t('recipebook.ingredients_en_special', 'Ingrédients en spécial')}:
                          </p>
                          <p className="text-xs text-emerald-300/70 mt-0.5 capitalize">
                            {recipe.matching_deals.slice(0, 3).join(', ')}
                          </p>
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewRecipe(recipe);
                          }}
                          variant="outline"
                          className="flex-1 border-purple-500/30 hover:border-purple-500/50"
                          size="sm"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          {t('recipebook.voir', 'Voir')}
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveRecipe(recipe);
                          }}
                          disabled={savingRecipe === recipe.title}
                          className="flex-1 bg-purple-500 hover:bg-purple-600 text-white"
                          size="sm"
                        >
                          {savingRecipe === recipe.title ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Plus className="w-4 h-4 mr-1" />
                          )}
                          {t('recipebook.ajouter', 'Ajouter')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>

            {/* Tip to go back to saved recipes */}
            <div className="mt-8 text-center">
              <p className="text-sm text-muted-foreground mb-2">
                {t('recipebook.les_recettes_ajoutees_apparaissent_dans_', 'Les recettes ajoutées apparaissent dans "Mes Recettes"')}
              </p>
              <Button
                variant="outline"
                onClick={() => setActiveMainTab('myRecipes')}
                className="border-primary/30 text-primary hover:bg-primary/10"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                {t('recipebook.voir_mes_recettes_sauvegardees', 'Voir mes recettes sauvegardées')}
              </Button>
            </div>
          </motion.div>
        )}

        {/* My Recipes Tab Content */}
        {activeMainTab === 'myRecipes' && (
          <>
            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-6"
            >
              <Card className="bg-card border-border">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row gap-3">
                    <div className="flex-1 flex gap-2">
                      <Input
                        placeholder={t('recipebook.rechercher_une_recette_sur_le_web', 'Rechercher une recette sur le web...')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleWebSearch()}
                        className="bg-secondary border-border"
                        data-testid="recipe-search-input"
                      />
                      <Button
                        onClick={handleWebSearch}
                        disabled={searching || !searchQuery.trim()}
                        className="bg-primary text-primary-foreground"
                        data-testid="recipe-search-btn"
                      >
                        {searching ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                      {searching && (
                        <Button
                          onClick={handleCancelSearch}
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          data-testid="recipe-search-cancel"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant={filterHighProtein ? 'default' : 'outline'}
                        onClick={() => setFilterHighProtein(!filterHighProtein)}
                        className={filterHighProtein ? 'bg-primary text-primary-foreground' : 'border-primary text-primary'}
                        size="sm"
                      >
                        <Zap className="w-4 h-4 mr-1" />
                        {t('recipebook.riche_en_proteines', 'Riche en protéines')}
                      </Button>
                    </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Search Progress Bar */}
          {searching && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-3 p-4 bg-card border border-border rounded-xl"
              data-testid="recipe-search-progress"
            >
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="w-4 h-4 text-primary animate-pulse" />
                <p className="text-sm text-foreground">{searchStatus}</p>
              </div>
              <Progress value={searchProgress} className="h-2" />
              {searchResults.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {searchResults.length} {t('recipebook.recettes_recues', 'recettes reçues')}
                </p>
              )}
            </motion.div>
          )}
        </motion.div>

        {/* My Recipes Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              {t('recipebook.mes_recettes', 'Mes Recettes')}
              <span className="text-muted-foreground text-sm font-normal">({recipes.length})</span>
            </h2>
            
            {/* Filters */}
            <div className="flex gap-2">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-32 bg-secondary border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label[language] || cat.label.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : recipes.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center">
                <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-30" />
                <p className="text-muted-foreground mb-2">
                  {t('recipebook.ton_livre_de_recettes_est_vide', 'Ton livre de recettes est vide')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('recipebook.recherche_des_recettes_pour_commencer', 'Recherche des recettes pour commencer!')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recipes.map((recipe, index) => (
                <div
                  key={recipe.recipe_id || recipe.id}
                >
                  <Card 
                    className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer h-full"
                    onClick={() => openRecipeDetail(recipe)}
                  >
                    {/* Recipe Image */}
                    <RecipeImage 
                      recipe={recipe} 
                      size="card" 
                      showControls={true} 
                      language={language}
                      onImageUpdate={handleImageUpdate}
                    />
                    
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-heading text-lg text-foreground">{recipe.title}</h3>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {recipe.has_deal_badge && (
                            <span data-testid="deal-badge" className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-bold border border-green-500/30 whitespace-nowrap">
                              $ {t('recipebook.aubaine', 'Aubaine')}
                            </span>
                          )}
                          {recipe.cooked_count > 0 && (
                            <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                              x{recipe.cooked_count}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {recipe.cultural_filtered && (
                        <div className="mb-2 px-2 py-1 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-400">
                          {t('recipebook.contient_des_ingredients_hors_de_vos_pre', '⚠️ Contient des ingrédients hors de vos préférences culturelles')}
                        </div>
                      )}
                      
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {recipe.description}
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        {recipe.prep_time && (
                          <span className="text-xs bg-secondary px-2 py-1 rounded flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {recipe.prep_time}
                          </span>
                        )}
                        {recipe.servings && (
                          <span className="text-xs bg-secondary px-2 py-1 rounded flex items-center gap-1">
                            <Users className="w-3 h-3" /> {recipe.servings}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex gap-3 text-sm">
                        <span className="text-primary font-bold">{recipe.protein_g || 0}g prot</span>
                        <span className="text-muted-foreground">{recipe.calories || 0} cal</span>
                        <span className="text-muted-foreground">{recipe.carbs_g || 0}g gluc</span>
                        {(!recipe.protein_g || recipe.protein_g === 0) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAnalyzeMacros(recipe);
                            }}
                            disabled={analyzingMacros === recipe.recipe_id}
                            className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"
                            title="Analyser les macros"
                          >
                            {analyzingMacros === recipe.recipe_id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Sparkles className="w-3 h-3" />
                            )}
                            Analyser
                          </button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Recipe Detail Dialog */}
        <Dialog open={showRecipeDetail} onOpenChange={(open) => {
          setShowRecipeDetail(open);
          // Don't clear selectedRecipe if we're opening delete dialog
          if (!open && !showDeleteDialog) {
            // Small delay to allow delete dialog to capture selectedRecipe
            setTimeout(() => {
              if (!showDeleteDialog) setSelectedRecipe(null);
            }, 100);
          }
        }}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedRecipe && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl uppercase tracking-tight flex items-center gap-2">
                    <ChefHat className="w-6 h-6 text-primary" />
                    {selectedRecipe.title}
                  </DialogTitle>
                  <DialogDescription>
                    {selectedRecipe.description}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                  {/* Recipe Image with controls */}
                  <RecipeImage 
                    recipe={selectedRecipe} 
                    size="detail" 
                    showControls={true} 
                    language={language}
                    onImageUpdate={handleImageUpdate}
                  />

                  {/* Macros */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-primary/10 p-3 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Protéines</p>
                      <p className="font-heading text-xl text-primary">{selectedRecipe.protein_g || 0}g</p>
                    </div>
                    <div className="bg-secondary p-3 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Calories</p>
                      <p className="font-heading text-xl">{selectedRecipe.calories || 0}</p>
                    </div>
                    <div className="bg-secondary p-3 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Glucides</p>
                      <p className="font-heading text-xl">{selectedRecipe.carbs_g || 0}g</p>
                    </div>
                    <div className="bg-secondary p-3 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Lipides</p>
                      <p className="font-heading text-xl">{selectedRecipe.fats_g || 0}g</p>
                    </div>
                  </div>

                  {/* AI Analyze Macros Button */}
                  {(!selectedRecipe.protein_g || selectedRecipe.protein_g === 0) && selectedRecipe.ingredients?.length > 0 && (
                    <Button
                      onClick={() => handleAnalyzeMacros(selectedRecipe)}
                      disabled={analyzingMacros === selectedRecipe.recipe_id}
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                    >
                      {analyzingMacros === selectedRecipe.recipe_id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('recipebook.analyse_en_cours', 'Analyse en cours...')}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          {t('recipebook.analyser_les_macros', 'Analyser les macros')}
                        </>
                      )}
                    </Button>
                  )}

                  {/* Smart Plate Analysis - Personal goals comparison */}
                  <SmartPlateAnalysis recipe={selectedRecipe} isPreview={false} />

                  {/* Savings Alert - Show if ingredients are on sale */}
                  {selectedRecipe.recipe_id && (
                    <RecipeSavingsAlert recipeId={selectedRecipe.recipe_id} language={language} />
                  )}

                  {/* Info */}
                  <div className="flex flex-wrap gap-3">
                    {selectedRecipe.prep_time && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        {t('recipebook.preparation', 'Préparation:')} {selectedRecipe.prep_time}
                      </div>
                    )}
                    {selectedRecipe.cook_time && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Flame className="w-4 h-4" />
                        {t('recipebook.cuisson', 'Cuisson:')} {selectedRecipe.cook_time}
                      </div>
                    )}
                    {selectedRecipe.servings && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="w-4 h-4" />
                        {selectedRecipe.servings}
                      </div>
                    )}
                  </div>

                  {/* Ingredients */}
                  {selectedRecipe.ingredients?.length > 0 && (
                    <div>
                      <h4 className="font-heading uppercase text-sm text-primary mb-2">
                        {t('recipebook.ingredients', 'Ingrédients')}
                      </h4>
                      <ul className="space-y-1">
                        {selectedRecipe.ingredients.map((ing, i) => (
                          <li key={i} className="text-sm text-foreground flex items-start gap-2">
                            <span className="text-primary">•</span>
                            {ing}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Instructions */}
                  {selectedRecipe.instructions?.length > 0 && (
                    <div>
                      <h4 className="font-heading uppercase text-sm text-primary mb-2">
                        {t('recipebook.instructions', 'Instructions')}
                      </h4>
                      <ol className="space-y-2">
                        {selectedRecipe.instructions.map((step, i) => (
                          <li key={i} className="text-sm text-foreground flex items-start gap-3">
                            <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">
                              {i + 1}
                            </span>
                            {step}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t border-border">
                    <Button
                      onClick={() => handleCookRecipe(selectedRecipe)}
                      disabled={cookingRecipe === (selectedRecipe.id || selectedRecipe.recipe_id)}
                      className="flex-1 bg-primary text-primary-foreground"
                      data-testid="cook-recipe-btn"
                    >
                      {cookingRecipe === (selectedRecipe.id || selectedRecipe.recipe_id) ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Utensils className="w-4 h-4 mr-2" />
                      )}
                      {t('recipebook.j_ai_cuisine_ca', 'J\\\'ai cuisiné ça!')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={openEditDialog}
                      className="border-primary text-primary hover:bg-primary/10"
                      data-testid="edit-recipe-btn"
                    >
                      <Edit3 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        // Keep selectedRecipe reference before closing detail dialog
                        setShowDeleteDialog(true);
                        setShowRecipeDetail(false);
                      }}
                      className="text-destructive border-destructive hover:bg-destructive/10"
                      data-testid="delete-recipe-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Recipe Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-primary" />
                {t('recipebook.modifier_la_recette', 'Modifier la recette')}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Title & Description */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    {t('recipebook.titre', 'Titre')}
                  </label>
                  <Input
                    value={editForm.title}
                    onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                    className="bg-secondary border-border"
                    data-testid="edit-recipe-title"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Description</label>
                  <Textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                    className="bg-secondary border-border min-h-[80px]"
                    data-testid="edit-recipe-description"
                  />
                </div>
              </div>

              {/* Macros */}
              <div>
                <p className="text-xs text-muted-foreground mb-2">
                  {t('recipebook.les_calories_sont_recalculees_automatiqu', '💡 Les calories sont recalculées automatiquement (Prot×4 + Gluc×4 + Lip×9)')
                  }
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {t('recipebook.proteines_g', 'Protéines (g)')}
                    </label>
                    <Input
                      type="number"
                      value={editForm.protein_g}
                      onChange={(e) => handleMacroChange('protein_g', e.target.value)}
                      className="bg-secondary border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {t('recipebook.glucides_g', 'Glucides (g)')}
                    </label>
                    <Input
                      type="number"
                      value={editForm.carbs_g}
                      onChange={(e) => handleMacroChange('carbs_g', e.target.value)}
                      className="bg-secondary border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {t('recipebook.lipides_g', 'Lipides (g)')}
                    </label>
                    <Input
                      type="number"
                      value={editForm.fats_g}
                      onChange={(e) => handleMacroChange('fats_g', e.target.value)}
                      className="bg-secondary border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground flex items-center gap-1">
                      Calories
                      <span className="text-xs text-primary">(auto)</span>
                    </label>
                    <Input
                      type="number"
                      value={editForm.calories}
                      readOnly
                      className="bg-primary/10 border-primary/30 text-primary font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* Ingredients */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">
                    {t('recipebook.ingredients', 'Ingrédients')}
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleAddIngredient}
                    className="text-primary"
                    data-testid="add-ingredient-btn"
                  >
                    <PlusCircle className="w-4 h-4 mr-1" />
                    {t('recipebook.ajouter', 'Ajouter')}
                  </Button>
                </div>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {editForm.ingredients?.map((ing, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        value={ing}
                        onChange={(e) => handleUpdateIngredient(index, e.target.value)}
                        placeholder={t('recipebook.ingredient_n_placeholder', 'Ingrédient {{n}}', { n: index + 1 })}
                        className="bg-secondary border-border flex-1"
                        data-testid={`ingredient-input-${index}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveIngredient(index)}
                        className="text-destructive hover:text-destructive"
                      >
                        <MinusCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Instructions */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">Instructions</label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleAddInstruction}
                    className="text-primary"
                    data-testid="add-instruction-btn"
                  >
                    <PlusCircle className="w-4 h-4 mr-1" />
                    {t('recipebook.ajouter', 'Ajouter')}
                  </Button>
                </div>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {editForm.instructions?.map((inst, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">
                        {index + 1}
                      </span>
                      <Textarea
                        value={inst}
                        onChange={(e) => handleUpdateInstruction(index, e.target.value)}
                        placeholder={t('recipebook.step_n_placeholder', 'Étape {{n}}', { n: index + 1 })}
                        className="bg-secondary border-border flex-1 min-h-[60px]"
                        data-testid={`instruction-input-${index}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveInstruction(index)}
                        className="text-destructive hover:text-destructive"
                      >
                        <MinusCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Save Button */}
              <div className="flex gap-3 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => setShowEditDialog(false)}
                  className="flex-1"
                >
                  <X className="w-4 h-4 mr-2" />
                  {t('recipebook.annuler', 'Annuler')}
                </Button>
                <Button
                  onClick={handleSaveRecipeEdit}
                  disabled={saving}
                  className="flex-1 bg-primary text-primary-foreground"
                  data-testid="save-recipe-edit-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {t('recipebook.sauvegarder', 'Sauvegarder')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) {
            setSelectedRecipe(null);
          }
        }}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading text-xl">
                {t('recipebook.supprimer_cette_recette', 'Supprimer cette recette?')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('recipebook.cette_action_est_irreversible_la_recette', 'Cette action est irréversible. La recette sera définitivement supprimée de ton livre.')
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-border">
                {t('recipebook.annuler', 'Annuler')}
              </AlertDialogCancel>
              <Button
                onClick={async (e) => {
                  e.preventDefault();
                  if (selectedRecipe) {
                    await handleDeleteRecipe(selectedRecipe.id || selectedRecipe.recipe_id);
                  }
                }}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="confirm-delete-recipe-btn"
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                {t('recipebook.supprimer', 'Supprimer')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
          </>
        )}

        {/* Recipe Preview Dialog (for search results) */}
        <Dialog open={!!previewRecipe} onOpenChange={() => setPreviewRecipe(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {previewRecipe && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-primary flex items-center gap-2">
                    <ChefHat className="w-6 h-6" />
                    {previewRecipe.title}
                  </DialogTitle>
                  <DialogDescription>{previewRecipe.description}</DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                  {/* Preview Image placeholder - AI generated after saving */}
                  <div className="h-40 w-full bg-gradient-to-br from-purple-500/10 via-blue-500/10 to-primary/10 rounded-xl flex flex-col items-center justify-center gap-2 border border-purple-500/20">
                    <ChefHat className="w-10 h-10 text-purple-400/50" />
                    <p className="text-xs text-muted-foreground text-center px-4">
                      {t('recipebook.une_image_sera_generee_apres_l_ajout_a_v', 'Une image sera générée après l\\\'ajout à votre livre')}
                    </p>
                  </div>

                  {/* Macros */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-primary/10 p-3 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Protéines</p>
                      <p className="font-heading text-xl text-primary">{previewRecipe.estimated_protein_g || '?'}g</p>
                    </div>
                    <div className="bg-secondary p-3 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Calories</p>
                      <p className="font-heading text-xl">{previewRecipe.estimated_calories || '?'}</p>
                    </div>
                    <div className="bg-secondary p-3 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Glucides</p>
                      <p className="font-heading text-xl">{previewRecipe.estimated_carbs_g || '?'}g</p>
                    </div>
                    <div className="bg-secondary p-3 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Lipides</p>
                      <p className="font-heading text-xl">{previewRecipe.estimated_fats_g || '?'}g</p>
                    </div>
                  </div>

                  {/* Smart Plate Analysis - Personal goals comparison */}
                  <SmartPlateAnalysis recipe={previewRecipe} isPreview={true} />

                  {/* Info */}
                  <div className="flex flex-wrap gap-3">
                    {previewRecipe.prep_time && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        {t('recipebook.preparation', 'Préparation:')} {previewRecipe.prep_time}
                      </div>
                    )}
                    {previewRecipe.cook_time && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Flame className="w-4 h-4" />
                        {t('recipebook.cuisson', 'Cuisson:')} {previewRecipe.cook_time}
                      </div>
                    )}
                    {previewRecipe.servings && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="w-4 h-4" />
                        {previewRecipe.servings}
                      </div>
                    )}
                  </div>

                  {/* Tags */}
                  {previewRecipe.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {previewRecipe.tags.map((tag, i) => (
                        <span key={i} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Ingredients */}
                  {previewRecipe.ingredients?.length > 0 && (
                    <div>
                      <h4 className="font-heading uppercase text-sm text-primary mb-2 flex items-center gap-2">
                        <Utensils className="w-4 h-4" />
                        {t('recipebook.ingredients', 'Ingrédients')}
                      </h4>
                      <ul className="space-y-1 bg-secondary/30 rounded-lg p-3">
                        {previewRecipe.ingredients.map((ing, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <span className="text-primary">•</span>
                            <span>{ing}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Instructions */}
                  {previewRecipe.instructions?.length > 0 && (
                    <div>
                      <h4 className="font-heading uppercase text-sm text-primary mb-2">
                        {t('recipebook.instructions', 'Instructions')}
                      </h4>
                      <ol className="space-y-3">
                        {previewRecipe.instructions.map((step, i) => (
                          <li key={i} className="flex gap-3 text-sm">
                            <span className="flex-shrink-0 w-6 h-6 bg-primary/20 text-primary rounded-full flex items-center justify-center text-xs font-bold">
                              {i + 1}
                            </span>
                            <span className="text-muted-foreground pt-0.5">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Add to Book Button */}
                  <div className="flex flex-col gap-3 pt-4 border-t border-border">
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => setPreviewRecipe(null)}
                        className="flex-1"
                      >
                        {t('recipebook.fermer', 'Fermer')}
                      </Button>
                      <Button
                        onClick={() => {
                          handleSaveRecipe(previewRecipe);
                          setPreviewRecipe(null);
                        }}
                        disabled={savingRecipe === previewRecipe.title}
                        className="flex-1 bg-primary text-primary-foreground"
                      >
                        {savingRecipe === previewRecipe.title ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4 mr-2" />
                        )}
                        {t('recipebook.ajouter_a_mon_livre', 'Ajouter à mon livre')}
                      </Button>
                    </div>
                    
                    {/* "Not what I'm looking for" button */}
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setPreviewRecipe(null);
                        handleWebSearch();
                      }}
                      disabled={searching}
                      className="w-full text-muted-foreground hover:text-primary"
                      data-testid="preview-more-suggestions-btn"
                    >
                      {searching ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      {t('recipebook.pas_ce_que_je_cherche_autres_suggestions', 'Pas ce que je cherche ? Autres suggestions')}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Macro Analysis Result Modal */}
        <Dialog open={!!macroAnalysisResult} onOpenChange={() => setMacroAnalysisResult(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-primary flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                {t('recipebook.analyse_nutritionnelle', 'Analyse Nutritionnelle')}
              </DialogTitle>
              <DialogDescription>
                {macroAnalysisResult?.recipeName}
              </DialogDescription>
            </DialogHeader>

            {macroAnalysisResult && (
              <div className="space-y-4 py-4">
                {/* Success message */}
                <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400">
                  <Check className="w-5 h-5" />
                  <span className="text-sm">{macroAnalysisResult.message}</span>
                </div>

                {/* Macros Grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-primary/10 p-4 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground mb-1">{t('recipebook.proteines', 'Protéines')}</p>
                    <p className="font-heading text-2xl text-primary">{macroAnalysisResult.nutrition?.protein_g || 0}g</p>
                  </div>
                  <div className="bg-secondary p-4 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground mb-1">{t('recipebook.calories', 'Calories')}</p>
                    <p className="font-heading text-2xl">{macroAnalysisResult.nutrition?.calories || 0}</p>
                  </div>
                  <div className="bg-amber-500/10 p-4 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground mb-1">{t('recipebook.glucides', 'Glucides')}</p>
                    <p className="font-heading text-2xl text-amber-400">{macroAnalysisResult.nutrition?.carbs_g || 0}g</p>
                  </div>
                  <div className="bg-orange-500/10 p-4 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground mb-1">{t('recipebook.lipides', 'Lipides')}</p>
                    <p className="font-heading text-2xl text-orange-400">{macroAnalysisResult.nutrition?.fats_g || 0}g</p>
                  </div>
                </div>

                {/* Fiber if available */}
                {macroAnalysisResult.nutrition?.fiber_g > 0 && (
                  <div className="bg-emerald-500/10 p-3 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground mb-1">{t('recipebook.fibres', 'Fibres')}</p>
                    <p className="font-heading text-xl text-emerald-400">{macroAnalysisResult.nutrition?.fiber_g}g</p>
                  </div>
                )}

                {/* Analysis Notes */}
                {macroAnalysisResult.notes && (
                  <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-blue-400 mb-2">
                      <ChefHat className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {t('recipebook.notes_du_chef', 'Notes du Chef')}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {macroAnalysisResult.notes}
                    </p>
                  </div>
                )}

                {/* Close Button */}
                <Button
                  onClick={() => setMacroAnalysisResult(null)}
                  className="w-full bg-primary text-primary-foreground"
                >
                  {t('recipebook.compris', 'Compris !')}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default RecipeBook;
